import * as zod from 'zod';

import { CartesianPose, ZERO_POSE } from '@sb/geometry';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types/CameraConfiguration';
import { CameraIntegration } from '@sb/integrations/types/cameraTypes';
import { ActionRequiredError, Step } from '@sb/remote-control/types';
import {
  DEFAULT_REGION_OF_INTEREST,
  LocateMethod,
  RegionOfInterest,
} from '@sb/routine-runner';

export namespace LocateStep {
  export const name = 'Locate';
  export const description = 'Locate via vision';
  export const librarySection = Step.LibrarySection.Vision;
  export const argumentKind = 'Locate';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    // For camera-bot-cpp mode
    cameraEquipmentId: zod.string().optional(),
    // For non-camera-bot-cpp mode
    camera: CameraIntegration.optional(),
    regionOfInterest: RegionOfInterest.default(DEFAULT_REGION_OF_INTEREST),
    method: LocateMethod,
    positionListName: zod.string().default('Locate step results'),
    resultsLimit: zod.number().default(10),
    transform: CartesianPose.default(ZERO_POSE),
    planeID: zod.string().optional(),
    objectHeight: zod.number().default(0),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({
    stepConfiguration,
    routine: { space },
    equipment,
    globalSpace,
  }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    if (!args.planeID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No workplane selected',
        fieldId: 'planeID',
      });
    }

    const allSpace = [...globalSpace, ...space];

    if (
      !allSpace.some(
        (item) => item.id === args.planeID && item.kind === 'plane',
      )
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Selected workplane is not valid',
        fieldId: 'planeID',
      });
    }

    if (!args.positionListName) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Position list name is not configured',
        fieldId: 'positionListName',
      });
    }

    // Validate camera equipment if it is set
    if (args.cameraEquipmentId) {
      const cameraEquipment = equipment.find(
        (e) => e.id === args.cameraEquipmentId,
      );

      if (cameraEquipment == null) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Camera equipment is not set',
          fieldId: 'cameraEquipmentId',
        });
      }

      if (cameraEquipment.config.kind !== 'Camera') {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Camera equipment must be a camera.',
          fieldId: 'cameraEquipmentId',
        });
      }

      const cameraConfig = cameraEquipment.config as CameraConfiguration;

      if (!cameraConfig.intrinsicsId) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Intrinsics on camera equipment must be set.',
          fieldId: 'cameraEquipmentId',
        });
      }

      if (!cameraConfig.extrinsicsId) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Extrinsics on camera equipment must be set.',
          fieldId: 'cameraEquipmentId',
        });
      }
    }

    const { method } = args;

    switch (method.kind) {
      case 'BlobDetection2D': {
        if (method.settings.maxThreshold < method.settings.minThreshold) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Max threshold may not be smaller than min threshold',
          });
        }

        if (method.settings.maxArea < method.settings.minArea) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Max area may not be smaller than min area',
          });
        }

        if (
          method.settings.filterByCircularity &&
          method.settings.maxCircularity < method.settings.minCircularity
        ) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Max circularity may not be smaller than min circularity',
          });
        }

        break;
      }

      case 'ShapeDetection2D': {
        if (method.settings.maxScale < method.settings.minScale) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Max scale may not be smaller than min scale',
          });
        }

        if (!method.templateImage.storageID) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Template image is not set',
          });
        }

        break;
      }

      case 'CustomCheckpointDetection2D': {
        if (!method.settings.gpuTaskRunnerId) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'No GPU Task Runner selected',
            fieldId: 'method.settings.gpuTaskRunnerId',
          });
        }

        const equipmentConfig = equipment.find(
          (e) => e.id === method.settings.gpuTaskRunnerId,
        );

        if (!equipmentConfig) {
          throw new ActionRequiredError({
            kind: 'missingEquipment',
            message: `Unable to find GPU Task Runner with id ${method.settings.gpuTaskRunnerId} in equipment manager.`,
            fieldId: 'method.settings.gpuTaskRunnerId',
          });
        }

        if (!method.settings.modelPath) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'No model selected',
            fieldId: 'method.settings.modelPath',
          });
        }

        if (!method.settings.modelType) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'No model type selected',
            fieldId: 'method.settings.modelType',
          });
        }

        break;
      }
      case 'TextPromptDetection2D': {
        if (!method.settings.textInput) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Text prompt input is empty',
            fieldId: 'method.settings.textInput',
          });
        }

        if (method.settings.gpuTaskRunnerId) {
          const equipmentConfig = equipment.find(
            (e) => e.id === method.settings.gpuTaskRunnerId,
          );

          if (!equipmentConfig) {
            throw new ActionRequiredError({
              kind: 'missingEquipment',
              message: `Unable to find GPU Task Runner with id ${method.settings.gpuTaskRunnerId} in equipment manager.`,
              fieldId: 'method.settings.gpuTaskRunnerId',
            });
          }
        }

        break;
      }

      default:
        break;
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'Locate',
      args: {
        camera: args.camera,
        cameraEquipmentId: args.cameraEquipmentId,
        regionOfInterest: args.regionOfInterest,
        method: args.method,
        positionListID: stepData.id,
        filters: {
          resultsLimit: args.resultsLimit,
        },
        transform: args.transform,
        planeID: args.planeID!,
        objectHeight: args.objectHeight,
      },
    };
  };
}

LocateStep satisfies Step.StepKindInfo;
